import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSave,
  faImage,
  faCalendarAlt,
  faFolderOpen,
  faTags,
  faHeading,
  faAlignLeft,
  faFileLines,
  faUser,
  faPlus,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';

const EditArticle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    subject: '',
    tags: '',
    editorId: '',
    postingDate: '',
  });

  // Liste des éditeurs
  const [editors, setEditors] = useState([]);

  // Multiple paragraphs state
  const [paragraphs, setParagraphs] = useState([
    { title: '', subtitle: '', text: '' }
  ]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageUrl, setCurrentImageUrl] = useState('');

  // Récupération des éditeurs
  useEffect(() => {
    async function fetchEditors() {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/editor', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEditors(res.data);
      } catch {
        setEditors([]);
      }
    }
    fetchEditors();
  }, []);

  // Fetch existing article
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`/article/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const article = res.data;

        // Parse paragraphs
        let parsedParagraphs = [];
        try {
          parsedParagraphs = Array.isArray(article.paragraphs)
            ? article.paragraphs
            : JSON.parse(article.paragraphs);
        } catch {
          parsedParagraphs = [{ title: '', subtitle: '', text: '' }];
        }

        setFormData({
          title: article.title || '',
          subtitle: article.subtitle || '',
          subject: article.subject || '',
          tags: Array.isArray(article.tags) ? article.tags.join(', ') : '',
          editorId: article.editor?.id || article.editorId || '',
          postingDate: toInputDateTime(article.postingDate),
        });

        setParagraphs(parsedParagraphs.length > 0 ? parsedParagraphs : [{ title: '', subtitle: '', text: '' }]);
        setCurrentImageUrl(article.imageUrl || '');
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        alert('Erreur lors du chargement de l\'article');
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  // Date formatting for DB
  const formatDateForDB = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Convert backend date to input datetime-local format
  const toInputDateTime = (dbDate) => {
    if (!dbDate) return '';
    const d = new Date(dbDate);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle paragraph changes
  const handleParagraphChange = (index, field, value) => {
    const updatedParagraphs = [...paragraphs];
    updatedParagraphs[index][field] = value;
    setParagraphs(updatedParagraphs);
  };

  // Add new paragraph
  const addParagraph = () => {
    setParagraphs([...paragraphs, { title: '', subtitle: '', text: '' }]);
  };

  // Remove paragraph
  const removeParagraph = (index) => {
    if (paragraphs.length > 1) {
      const updatedParagraphs = paragraphs.filter((_, i) => i !== index);
      setParagraphs(updatedParagraphs);
    } else {
      alert('Au moins un paragraphe est requis');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 8 * 1024 * 1024) {
        alert('Le fichier est trop volumineux (max 8 Mo)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const triggerFileSelect = () => fileInputRef.current.click();

  const onCancel = () => navigate('/admin/articles');

  const formatErrorMessage = (value) => {
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(formatErrorMessage).join(', ');
    if (value && typeof value === 'object') {
      return Object.entries(value)
        .map(([field, message]) => `${field}: ${formatErrorMessage(message)}`)
        .join(', ');
    }
    return String(value || "Erreur lors de la mise à jour de l'article.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const validParagraphs = paragraphs.filter((p) => p.text && p.text.trim() !== '');
    if (validParagraphs.length === 0) {
      alert('Au moins un paragraphe avec du texte est requis');
      setIsSubmitting(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const data = new FormData();
      data.append('title', formData.title || '');
      data.append('subtitle', formData.subtitle || '');
      data.append('subject', formData.subject || '');
      const tags = (formData.tags || '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
      tags.forEach((tag) => data.append('tags', tag));
      data.append('paragraphs', JSON.stringify(validParagraphs));
      data.append('editorId', formData.editorId || '');

      if (selectedFile) {
        data.append('imageUrl', selectedFile);
      }

      const dateToSend = formData.postingDate
        ? formatDateForDB(formData.postingDate)
        : formatDateForDB(new Date());
      data.append('postingDate', dateToSend);

      await axios.patch(`/article/${id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      alert('Article mis à jour avec succès !');
      navigate('/admin/articles');
    } catch (err) {
      console.error('Erreur mise à jour:', err.response?.data || err.message);
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Erreur lors de la mise à jour de l'article.";
      alert(formatErrorMessage(errorMsg));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <p>Chargement...</p>;

  return (
    <div className="form-container">
      <div className="form-header">
        <button className="back-button" type="button" onClick={onCancel}>
          <FontAwesomeIcon icon={faArrowLeft} /> Retour
        </button>
        <h3>Éditer l'article</h3>
      </div>
      <div className="form-card">
        <form onSubmit={handleSubmit} className="project-form">
          <div className="form-section">
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faHeading} className="label-icon" /> Titre *
              </label>
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Titre de l'article"
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faAlignLeft} className="label-icon" /> Sous-titre *
              </label>
              <input
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                placeholder="Sous-titre"
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faFileLines} className="label-icon" /> Sujet *
              </label>
              <input
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Sujet de l'article"
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faTags} className="label-icon" /> Tags
                (séparés par des virgules)
              </label>
              <input
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="tag1, tag2, tag3"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faCalendarAlt} className="label-icon" /> Date de publication
              </label>
              <input
                type="datetime-local"
                name="postingDate"
                value={formData.postingDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faUser} className="label-icon" /> Éditeur *
              </label>
              <select
                name="editorId"
                value={formData.editorId}
                onChange={handleChange}
                className="form-input"
                required
              >
                <option value="">Sélectionnez un éditeur</option>
                {editors.map((editor) => (
                  <option key={editor.id} value={editor.id}>
                    {editor.username || editor.name || editor.email || `Éditeur #${editor.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                <FontAwesomeIcon icon={faFileLines} className="label-icon" /> Paragraphes *
              </label>
              <p style={{ margin: '0 0 16px', color: '#6b6b82', lineHeight: 1.5 }}>
                Structure conseillée : un titre clair, un sous-titre facultatif et un texte de 80 à 200 mots. Créez un nouveau bloc pour chaque idée principale.
              </p>
              {paragraphs.map((paragraph, index) => (
                <div
                  key={index}
                  style={{
                    border: '1px solid #444',
                    padding: '15px',
                    marginBottom: '15px',
                    borderRadius: '5px',
                    position: 'relative',
                  }}
                >
                  <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#fff' }}>
                    Paragraphe {index + 1}
                  </div>
                  <input
                    value={paragraph.title}
                    onChange={e => handleParagraphChange(index, 'title', e.target.value)}
                    placeholder="Titre du paragraphe"
                    className="form-input"
                    style={{ marginBottom: '10px' }}
                  />
                  <input
                    value={paragraph.subtitle}
                    onChange={e => handleParagraphChange(index, 'subtitle', e.target.value)}
                    placeholder="Sous-titre du paragraphe"
                    className="form-input"
                    style={{ marginBottom: '10px' }}
                  />
                  <textarea
                    value={paragraph.text}
                    onChange={e => handleParagraphChange(index, 'text', e.target.value)}
                    placeholder="Texte du paragraphe *"
                    className="form-input"
                    required
                    rows={5}
                  />
                  {paragraphs.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeParagraph(index)}
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addParagraph}
                style={{
                  background: '#ffffffff',
                  color: '#0F0A3C',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  marginTop: '10px',
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> Ajouter un paragraphe
              </button>
            </div>
            <div
              className="form-group"
              style={{ display: 'flex', gap: '5px', alignItems: 'flex-start' }}
            >
              <div style={{ flexGrow: 1 }}>
                <label className="form-label">
                  <FontAwesomeIcon icon={faImage} className="label-icon" /> Image
                </label>
                {currentImageUrl && !selectedFile && (
                  <div style={{ marginBottom: '10px' }}>
                    <img
                      src={`/${currentImageUrl}`}
                      alt="Current"
                      style={{ maxWidth: '200px', maxHeight: '200px', display: 'block' }}
                    />
                    <small>Image actuelle</small>
                  </div>
                )}
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="text"
                    readOnly
                    value={selectedFile ? selectedFile.name : 'Aucun nouveau fichier sélectionné'}
                    placeholder="Choisissez un fichier"
                    className="form-input"
                    style={{ width: '100%', paddingRight: '40px', boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={triggerFileSelect}
                    className="btn-browse"
                    style={{
                      position: 'absolute',
                      right: '5px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      height: '30px',
                      padding: '0 10px',
                      cursor: 'pointer',
                      border: 'none',
                      background: '#ddd',
                      borderRadius: '3px',
                    }}
                  >
                    <FontAwesomeIcon icon={faFolderOpen} />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".png,.jpeg,.jpg,.svg"
                    onChange={handleFileSelect}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-save" disabled={isSubmitting}>
              <FontAwesomeIcon icon={faSave} />
              {isSubmitting ? ' Mise à jour...' : ' Enregistrer'}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditArticle;
